import { PlusOutlined } from "@ant-design/icons";
import { Button, Form, Input, InputNumber, Radio, Space } from "antd";
import TextArea from "antd/es/input/TextArea";
import { useEffect } from "react";
import ReactQuill from "react-quill";
import { Link, useNavigate, useParams } from "react-router-dom";
import DeleteIcon from "../../../assets/icons/DeleteIcon";
import CardTitle from "../../../components/common/CardTitle";
import CustomCard from "../../../components/common/CustomCard";
import SuccessModal from "../../../components/common/SuccessModal";
import EmployeeFileUpload from "../../../components/Employees/EmployeeFileUpload";
import Loader from "../../../components/ui/Loader";
import {
  quillToolbarFormats,
  quillToolbarModules,
} from "../../../constants/config";
import {
  useCreateProductMutation,
  useDeleteProductFeatureMutation,
  useGetSingleProductQuery,
  useUpdateProductMutation,
} from "../../../features/portal/Products/productsApi";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import SidebarLayout from "../../../layouts/SidebarLayout";

export default function AddEditProduct() {
  const { open, handleOpen, handleClose } = useModal();
  const [form] = Form.useForm();
  const { id } = useParams();
  const navigate = useNavigate();
  const backLink = "/admin/products/product-list";

  const [createProduct, { isLoading, isError, error, isSuccess }] =
    useCreateProductMutation();
  const [
    updateProduct,
    {
      isLoading: isUpdateProductLoading,
      isError: isUpdateProductError,
      error: updateProductError,
      isSuccess: isUpdateProductSuccess,
    },
  ] = useUpdateProductMutation();
  const {
    isLoading: isSingleProductLoading,
    isError: isSingleProductError,
    error: singleProductError,
    data: getSingleProduct,
  } = useGetSingleProductQuery(id ?? "", {
    skip: !id,
  });

  const [
    deleteProductFeature,
    { isError: deleteProductFeatureIsError, error: deleteProductFeatureError },
  ] = useDeleteProductFeatureMutation();

  useError(isError, error, "Product create fail");
  useError(isUpdateProductError, updateProductError, "Product create fail");
  useError(isSingleProductError, singleProductError, "Get single product fail");
  useError(
    deleteProductFeatureIsError,
    deleteProductFeatureError,
    "Product feature delete fail"
  );

  const singleData = getSingleProduct?.data;

  const onFinish = async (values: {
    is_active: any;
    redirectUri?: string;
    [key: string]: any;
  }) => {
    const hasExternalLink =
      typeof values.redirectUri === "string" &&
      /^(https?:\/\/|www\.)/i.test(values.redirectUri.trim());

    const statusData =
      values.is_active === 0
        ? "draft"
        : values.is_active === true || values.is_active === false
        ? null
        : undefined;

    const newData = {
      ...values,
      is_active: values.is_active,
      status: statusData,
      redirectToAnotherWebsite: !!hasExternalLink,
    };

    if (id) {
      await updateProduct({ id, data: newData });
    } else {
      await createProduct(newData);
    }
  };

  const handleOkay = () => {
    handleClose();
    navigate(`/admin/products/product-list`);
  };

  useEffect(() => {
    if (id && singleData) {
      form.setFieldsValue({
        name: singleData?.name,
        keycloakClientId: singleData?.keycloakClientId,
        redirectUri: singleData?.redirectUri,
        description: singleData?.description,
        sort_order: singleData?.sort_order,
        is_dynamic_key: singleData?.is_dynamic_key === true,

        is_active:
          singleData?.status === "draft"
            ? 0
            : singleData?.status === null
            ? singleData?.is_active === true
              ? true
              : singleData?.is_active === false
              ? false
              : undefined
            : undefined,

        logo: singleData?.logo,
        redirectToAnotherWebsite: singleData?.redirectToAnotherWebsite,

        // 👇 Change "projectFeatures" → "features"
        // features: singleData?.projectFeatures?.map((item: any) => ({
        //   id: item.id,
        //   feature_image: item.feature_image,
        //   details: item.details,
        //   button: item.button,
        //   button_link: item.button_link,
        // })),

        features:
          singleData?.projectFeatures?.length > 0
            ? singleData.projectFeatures.map((item: any) => ({
                id: item.id,
                feature_image: item.feature_image,
                details: item.details,
                button: item.button,
                button_link: item.button_link,
              }))
            : [{}],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, singleData, form]);

  useEffect(() => {
    if (isSuccess) {
      form.setFieldsValue({});
      handleOpen({ type: "ADD" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  useEffect(() => {
    if (isUpdateProductSuccess) {
      // form.setFieldsValue({});
      handleOpen({ type: "UPDATE" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUpdateProductSuccess]);

  useEffect(() => {
    form.setFieldsValue({
      features: [{}],
    });
  }, [form]);

  if (isSingleProductLoading) {
    return <Loader />;
  }

  return (
    <>
      {open?.type === "ADD" && (
        <SuccessModal
          open={open?.open}
          handleClose={handleClose}
          handleOkay={handleOkay}
          data={open?.data}
          title="Successfully Added"
        />
      )}

      {open?.type === "UPDATE" && (
        <SuccessModal
          open={open?.open}
          handleClose={handleClose}
          handleOkay={handleOkay}
          data={open?.data}
          title="Successfully Updated"
        />
      )}

      <SidebarLayout
        title={`${id ? "Edit Product Info" : "Add New Product"}`}
        backLink={backLink}
      >
        <Form
          className="custom-form"
          layout="vertical"
          requiredMark="optional"
          size="large"
          onFinish={onFinish}
          form={form}
          // initialValues={{
          //   features: [{}],
          // }}
        >
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-5">
              <CustomCard className=" p-6 pb-0 rounded-b-none">
                <CardTitle className="mb-2.5">
                  Product Onboarding Information
                </CardTitle>
                <Form.Item
                  name="name"
                  label="Title"
                  className="mb-4"
                  rules={[
                    {
                      required: true,
                      message: "Please enter a title",
                    },
                  ]}
                >
                  <Input size="large" placeholder="Select title" />
                </Form.Item>

                {/* <Form.Item
                label="Keycloak Client ID"
                name="keycloakClientId"
                rules={[
                  {
                    required: true,
                    message: "Please enter the Keycloak Client ID",
                  },
                ]}
              >
                <Input placeholder="Enter Keycloak Client ID" />
              </Form.Item> */}

                <Form.Item
                  label="Redirect URL"
                  name="redirectUri"
                  rules={[
                    {
                      required: true,
                      message: "Please enter the redirect URL",
                    },
                  ]}
                >
                  <Input placeholder="https://example.com" />
                </Form.Item>

                {/* <Form.Item
                label="Dynamic Id"
                name="dynamicId"
                // rules={[
                //   {
                //     required: true,
                //     message: "Please enter the dynamic ID",
                //   },
                // ]}
              >
                <InputNumber
                  placeholder="Enter the dynamic ID"
                  style={{ width: "100%" }}
                />
              </Form.Item>

              <Form.Item
                label="Dynamic Keyword"
                name="dyncamicKeyword"
                // rules={[
                //   {
                //     required: true,
                //     message: "Please enter the dyncamic keyword",
                //   },
                // ]}
              >
                <Input placeholder="Enter the dynamic keyword" />
              </Form.Item> */}

                <Form.Item
                  name="description"
                  label="Short Description"
                  className="mb-4"
                  rules={[
                    {
                      required: true,
                      message: "Please enter description",
                    },
                  ]}
                >
                  <TextArea
                    rows={3}
                    size="large"
                    placeholder="Short description"
                  />
                </Form.Item>

                <Form.Item
                  label="Sort Order Number"
                  name="sort_order"
                  rules={[
                    {
                      required: true,
                      message: "Please enter a sort order number",
                    },
                    {
                      validator: (_, value) => {
                        if (
                          value === undefined ||
                          value === null ||
                          value === ""
                        ) {
                          return Promise.resolve();
                        }

                        // Convert to number safely
                        const numValue = Number(value);

                        // ❌ Non-numeric input
                        if (isNaN(numValue)) {
                          return Promise.reject(
                            "Please enter only numeric values"
                          );
                        }

                        // ❌ Negative number
                        if (numValue < 0) {
                          return Promise.reject(
                            "Negative numbers are not allowed"
                          );
                        }

                        // ❌ Decimal number
                        if (!Number.isInteger(numValue)) {
                          return Promise.reject(
                            "Decimal numbers are not allowed"
                          );
                        }

                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <InputNumber
                    placeholder="Enter sort order number"
                    style={{ width: "100%" }}
                    type="number"
                  />
                </Form.Item>

                <Form.Item
                  name="logo"
                  label="Product Logo"
                  className="mb-4 "
                  valuePropName="fileUrl"
                  rules={[
                    {
                      required: true,
                      message: "Please upload Product Logo",
                    },
                  ]}
                >
                  <EmployeeFileUpload
                    form={form}
                    accept=".png, .jpg, .jpeg"
                    name="logo"
                    onChange={(url) =>
                      form.setFieldsValue({
                        logo: url,
                      })
                    }
                  />
                </Form.Item>

                {/* <Form.Item
                name="is_dynamic_key"
                label="Dynamic Keyword"
                initialValue={false}
                rules={[
                  {
                    required: true,
                    message: "Please select the system status",
                  },
                ]}
              >
                <Radio.Group>
                  <Radio value={true}>Yes</Radio>
                  <Radio value={false}>No</Radio>
                </Radio.Group>
              </Form.Item> */}

                <Form.Item
                  name="is_active"
                  label="System Status"
                  rules={[
                    {
                      validator: (_, value) => {
                        if (value === undefined) {
                          return Promise.reject(
                            "Please select the system status"
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Radio.Group>
                    <Radio value={true}>Active</Radio>
                    <Radio value={false}>Inactive</Radio>
                    <Radio value={0}>Draft</Radio>
                  </Radio.Group>
                </Form.Item>
              </CustomCard>

              <div>
                <CustomCard className=" p-6 pb-0 rounded-b-none">
                  <CardTitle className="mb-2.5">
                    Product Features Details
                  </CardTitle>

                  <Form.List name="features">
                    {(fields, { add, remove }) => (
                      <>
                        {fields.map(({ key, name, ...restField }, index) => {
                          const feature = form.getFieldValue("features")[name];
                          return (
                            <div key={key}>
                              <Form.Item
                                {...restField}
                                label="Feature Image"
                                name={[name, "feature_image"]}
                                className="mb-4 "
                                valuePropName="fileUrl"
                              >
                                <EmployeeFileUpload
                                  form={form}
                                  accept=".png, .jpg, .jpeg"
                                  name={`features.${name}.feature_image`}
                                  onChange={(url) =>
                                    form?.setFieldsValue({
                                      features: {
                                        [name]: {
                                          feature_image: url,
                                        },
                                      },
                                    })
                                  }
                                />
                              </Form.Item>

                              <Form.Item
                                {...restField}
                                label="Features Details"
                                name={[name, "details"]}
                                // rules={[
                                //   {
                                //     required: true,
                                //     message: "Please enter features details",
                                //   },
                                // ]}
                              >
                                <ReactQuill
                                  className="h-[250px] mb-11"
                                  theme="snow"
                                  formats={quillToolbarFormats}
                                  modules={quillToolbarModules}
                                  value={form.getFieldValue([
                                    "features",
                                    name,
                                    "details",
                                  ])}
                                  onChange={(val: any) =>
                                    form.setFields([
                                      {
                                        name: ["features", name, "details"],
                                        value: val,
                                      },
                                    ])
                                  }
                                />
                              </Form.Item>

                              <Form.Item
                                {...restField}
                                label="Button Name"
                                name={[name, "button"]}
                                className="mb-4 mt-16"
                              >
                                <Input size="large" placeholder="Button Name" />
                              </Form.Item>

                              <Form.Item
                                {...restField}
                                label="Button Link"
                                name={[name, "button_link"]}
                              >
                                <Input placeholder="https://example.com" />
                              </Form.Item>

                              <Button
                                className="action-button mt-5 mb-5"
                                size="small"
                                icon={<DeleteIcon />}
                                onClick={() => {
                                  if (feature?.id) {
                                    deleteProductFeature({
                                      id: id,
                                      featureId: feature.id,
                                    });
                                    remove(name);
                                  } else {
                                    remove(name);
                                  }
                                }}
                              />
                            </div>
                          );
                        })}
                        <div className="mt-1 flex justify-start">
                          <Button
                            onClick={() => add()}
                            className="border-none text-[#FE7235]"
                            icon={<PlusOutlined />}
                          >
                            Add More
                          </Button>
                        </div>
                      </>
                    )}
                  </Form.List>
                </CustomCard>

                {/* sticky bottom-0 */}
                <div className="p-6 rounded-md rounded-t-none bg-white border border-t-0 border-light">
                  <Space className="w-full mt-2" size={12}>
                    <Link to={backLink}>
                      <Button
                        className="normal-dark-button modal-button"
                        size="middle"
                      >
                        Cancel
                      </Button>
                    </Link>
                    <Button
                      className="modal-button"
                      type="primary"
                      htmlType="submit"
                      size="middle"
                      loading={isLoading || isUpdateProductLoading}
                    >
                      {id ? "Update" : "Save"}
                    </Button>
                  </Space>
                </div>
              </div>
            </div>
          </>
        </Form>
      </SidebarLayout>
    </>
  );
}

export const targetTypes = [
  { label: "Live", value: "live" },
  { label: "Maintenance Mode", value: "maintenance mode" },
];
